/**
 * Centralized Energy Flow layout logic per docs/implementation/ENERGY_FLOW_VISUALIZATION.md.
 * Inputs: pv_kw, battery_kw, grid_kw (positive = export, negative = import), load_kw.
 * Output: layoutType, ordered nodes, flows, labels, colors. Layout only changes after state persists ≥ 30s.
 */

import { useRef, useMemo, useEffect, useState } from 'react';

/** kW threshold for battery role (Sink/Source/Idle) and for PV/grid "idle". */
export const BATTERY_LAYOUT_THRESHOLD_KW = 0.3;

/** ms layout must persist before we switch (stability). */
export const LAYOUT_STABILITY_MS = 30_000;

export type EnergyFlowLayoutType = 'solar-led' | 'grid-led' | 'battery-led';

export type FlowNodeId = 'solar' | 'grid-import' | 'grid-export' | 'battery' | 'home' | 'gen';

export interface EnergyFlowNode {
  id: FlowNodeId;
  label: string;
  /** Display kW (always ≥ 0 for grid). */
  kw: number;
  /** CSS color class or token name for border/text. */
  color: string;
  visible: boolean;
  dimmed?: boolean;
}

export interface EnergyFlowEdge {
  from: FlowNodeId;
  to: FlowNodeId;
  /** Magnitude in kW for thickness. */
  kw: number;
  color: string;
}

export interface EnergyFlowLayoutResult {
  layoutType: EnergyFlowLayoutType;
  /** Left-to-right ordered nodes for main row (excluding optional grid-export branch). */
  nodes: EnergyFlowNode[];
  /** Edges to draw (left → right). */
  flows: EnergyFlowEdge[];
  /** Grid: never signed; label encodes direction. */
  gridLabel: 'Grid Import' | 'Grid Export' | null;
  /** Grid value: always positive (kW). */
  gridKwDisplay: number;
  batteryLabel: 'Charging' | 'Discharging' | 'Idle';
  /** Battery role for styling. */
  batteryRole: 'sink' | 'source' | 'idle';
}

function computeCandidateLayout(params: {
  pvKw: number;
  batteryKw: number;
  gridKw: number | null;
  loadKw: number;
  batteryStateOverride?: 'Charging' | 'Discharging' | 'Idle';
}): EnergyFlowLayoutType {
  const { pvKw, batteryKw, gridKw, loadKw, batteryStateOverride } = params;
  const t = BATTERY_LAYOUT_THRESHOLD_KW;
  const gridNeg = gridKw != null && gridKw < -t;
  const gridPos = gridKw != null && gridKw > t;
  const pvActive = pvKw > t;
  const batteryDischarging =
    batteryStateOverride === 'Discharging' || (batteryStateOverride == null && batteryKw < -t);
  const gridIdle = gridKw == null || Math.abs(gridKw) < t;

  // B) Grid-Led: importing
  if (gridNeg) return 'grid-led';

  // C) Battery-Led: islanded/night
  if (batteryDischarging && !pvActive && gridIdle) return 'battery-led';

  // A) Solar-Led: daytime, export or idle grid
  return 'solar-led';
}

function buildLayout(
  layoutType: EnergyFlowLayoutType,
  params: {
    pvKw: number;
    batteryKw: number;
    gridKw: number | null;
    loadKw: number;
    genKw: number | null;
    batteryStateOverride?: 'Charging' | 'Discharging' | 'Idle';
  }
): EnergyFlowLayoutResult {
  const { pvKw, batteryKw, gridKw, loadKw, genKw, batteryStateOverride } = params;
  const t = BATTERY_LAYOUT_THRESHOLD_KW;

  const batteryRole: 'sink' | 'source' | 'idle' =
    batteryStateOverride != null
      ? batteryStateOverride === 'Charging'
        ? 'sink'
        : batteryStateOverride === 'Discharging'
          ? 'source'
          : 'idle'
      : batteryKw > t
        ? 'sink'
        : batteryKw < -t
          ? 'source'
          : 'idle';
  const batteryLabel: 'Charging' | 'Discharging' | 'Idle' =
    batteryStateOverride ?? (batteryRole === 'sink' ? 'Charging' : batteryRole === 'source' ? 'Discharging' : 'Idle');

  const isImport = gridKw != null && gridKw < 0;
  const isExport = gridKw != null && gridKw > 0;
  const gridLabel: 'Grid Import' | 'Grid Export' | null = isImport
    ? 'Grid Import'
    : isExport
      ? 'Grid Export'
      : null;
  const gridKwDisplay = gridKw != null ? Math.abs(gridKw) : 0;

  const nodes: EnergyFlowNode[] = [];
  const flows: EnergyFlowEdge[] = [];

  const solarNode: EnergyFlowNode = {
    id: 'solar',
    label: 'Solar',
    kw: pvKw,
    color: 'wt-solar-gold',
    visible: true,
    dimmed: false,
  };
  const batteryNode: EnergyFlowNode = {
    id: 'battery',
    label: 'Battery',
    kw: Math.abs(batteryKw),
    color: batteryRole === 'sink' ? 'wt-green-primary' : batteryRole === 'source' ? 'destructive' : 'border',
    visible: true,
  };
  const homeNode: EnergyFlowNode = {
    id: 'home',
    label: 'Home',
    kw: loadKw,
    color: 'foreground',
    visible: true,
  };

  if (layoutType === 'solar-led') {
    solarNode.dimmed = false;
    nodes.push(solarNode, batteryNode, homeNode);
    if (pvKw > t) flows.push({ from: 'solar', to: 'battery', kw: pvKw, color: 'wt-green-primary' });
    if (loadKw > t) flows.push({ from: 'battery', to: 'home', kw: loadKw, color: batteryRole === 'source' ? 'destructive' : 'wt-green-primary' });
    // Grid Export on the right when exporting (per ENERGY_FLOW_VISUALIZATION.md)
    if (gridKwDisplay > t && isExport) {
      nodes.push({
        id: 'grid-export',
        label: 'Grid Export',
        kw: gridKwDisplay,
        color: 'wt-green-primary',
        visible: true,
      });
      flows.push({ from: 'home', to: 'grid-export', kw: gridKwDisplay, color: 'wt-green-primary' });
    }
  } else if (layoutType === 'grid-led') {
    nodes.push(
      {
        id: 'grid-import',
        label: 'Grid Import',
        kw: gridKwDisplay,
        color: 'wt-amber-primary',
        visible: true,
      },
      batteryNode,
      homeNode
    );
    if (gridKwDisplay > t) flows.push({ from: 'grid-import', to: 'battery', kw: gridKwDisplay, color: 'wt-amber-primary' });
    if (loadKw > t) flows.push({ from: 'battery', to: 'home', kw: loadKw, color: batteryRole === 'source' ? 'destructive' : 'wt-green-primary' });
    // Solar not in main row when grid-led; show dimmed in footer if needed
  } else {
    // battery-led
    nodes.push(batteryNode, homeNode);
    if (loadKw > t) flows.push({ from: 'battery', to: 'home', kw: loadKw, color: 'destructive' });
    solarNode.visible = false;
    // Grid hidden; we don't add to nodes for main row
  }

  return {
    layoutType,
    nodes,
    flows,
    gridLabel,
    gridKwDisplay,
    batteryLabel,
    batteryRole,
  };
}

export interface UseEnergyFlowLayoutParams {
  /** Solar power in kW */
  pvKw: number;
  /** Battery power in kW (positive = charging, negative = discharging) */
  batteryKw: number;
  /** Grid power in kW (positive = export, negative = import); null if disconnected */
  gridKw: number | null;
  /** Load power in kW */
  loadKw: number;
  /** Generator kW (optional; for display only) */
  genKw?: number | null;
  /** When set (e.g. from API flow flags), use for battery label/role instead of sign of batteryKw */
  batteryStateOverride?: 'Charging' | 'Discharging' | 'Idle';
}

/**
 * Returns stable Energy Flow layout: layout only changes after the candidate state
 * has persisted for LAYOUT_STABILITY_MS (30s). Labels and values conform to
 * ENERGY_FLOW_VISUALIZATION (Grid Import/Export, positive values, no signed grid).
 */
export function useEnergyFlowLayout(params: UseEnergyFlowLayoutParams): EnergyFlowLayoutResult {
  const { pvKw, batteryKw, gridKw, loadKw, genKw = null, batteryStateOverride } = params;

  const candidate = useMemo(
    () => computeCandidateLayout({ pvKw, batteryKw, gridKw, loadKw, batteryStateOverride }),
    [pvKw, batteryKw, gridKw, loadKw, batteryStateOverride]
  );

  const [committed, setCommitted] = useState<EnergyFlowLayoutType>(() => candidate);
  const candidateRef = useRef<EnergyFlowLayoutType>(candidate);
  const firstSeenAtRef = useRef<number>(Date.now());

  useEffect(() => {
    if (candidate === committed) return;

    if (candidateRef.current !== candidate) {
      candidateRef.current = candidate;
      firstSeenAtRef.current = Date.now();
    }

    // Immediately commit when solar comes up (battery-led → solar-led).
    // This is the normal daytime progression and shouldn't be delayed.
    if (candidate === 'solar-led' && committed === 'battery-led') {
      setCommitted(candidate);
      return;
    }

    // For other transitions, wait for stability period then commit via timeout.
    const elapsed = Date.now() - firstSeenAtRef.current;
    const remaining = Math.max(0, LAYOUT_STABILITY_MS - elapsed);
    const timer = setTimeout(() => setCommitted(candidate), remaining);
    return () => clearTimeout(timer);
  }, [candidate, committed]);

  return useMemo(
    () =>
      buildLayout(committed, {
        pvKw,
        batteryKw,
        gridKw,
        loadKw,
        genKw,
        batteryStateOverride,
      }),
    [committed, pvKw, batteryKw, gridKw, loadKw, genKw, batteryStateOverride]
  );
}
